/**
 * RFM Score Table
 * Shows top customers with their RFM scores
 */

import { RFMScore, SEGMENT_COLORS, SEGMENT_LABELS } from '../../types/analytics';
import { motion } from 'framer-motion';

interface RFMScoreTableProps {
  customers: RFMScore[];
  title?: string;
  limit?: number;
}

export function RFMScoreTable({ customers, title = 'Top Clienti', limit = 10 }: RFMScoreTableProps) {
  const displayCustomers = customers.slice(0, limit);

  if (displayCustomers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-slate-400">
        Nessun cliente trovato
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {title && <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
          <thead className="bg-gray-50 dark:bg-slate-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Segmento
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                RFM Score
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Ordini
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Lifetime Value
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Ultimo Ordine
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-900/30 divide-y divide-gray-200 dark:divide-slate-700/50">
            {displayCustomers.map((customer, index) => (
              <motion.tr
                key={customer.customerId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {customer.customerName || customer.customerEmail || customer.customerId.slice(0, 8)}
                    </div>
                    {customer.customerEmail && (
                      <div className="text-xs text-gray-500 dark:text-slate-400">{customer.customerEmail}</div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shadow-sm dark:ring-1 dark:ring-white/10"
                    style={{
                      backgroundColor: `${SEGMENT_COLORS[customer.segment]}20`,
                      color: SEGMENT_COLORS[customer.segment],
                    }}
                  >
                    {SEGMENT_LABELS[customer.segment]}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{customer.rfmScore}</span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">/15</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-500 dark:text-slate-400">
                    <span title="Recency">R:{customer.recencyScore}</span>
                    <span title="Frequency">F:{customer.frequencyScore}</span>
                    <span title="Monetary">M:{customer.monetaryScore}</span>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-900 dark:text-white">{customer.totalOrders}</span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    €{customer.lifetimeValue.toFixed(2)}
                  </span>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    €{customer.averageOrderValue.toFixed(2)} medio
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-900 dark:text-white">
                    {customer.daysSinceLastOrder} giorni fa
                  </span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
